import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from './ChildrenContext';
import PropTypes from 'prop-types';
import runtimeConfig from '../config/runtimeConfig';
import template from '../helpers/template';

const pageRange = (selected, numPages, num) => {
    let selectedPos = Math.ceil(num / 2);
    let start = (selected < selectedPos) ? 1 : selected - selectedPos + 1;
    let len = (numPages < start + num - 1) ? numPages - start + 1 : num;

    return Array
        .apply(null, Array(len))
        .map((u, i) => start + i);
};

const PaginationItem = ({className, page, display, loading = false, onPageChange}) => {
    const handleClick = useCallback(() => {
        onPageChange(page);
    }, [onPageChange, page]);

    return (
        <button
            className={'btn btn-outline btn-success' + (className ? ' ' + className : '')}
            onClick={handleClick}
            dangerouslySetInnerHTML={{__html: display + ''}}
            type="button"
            disabled={loading}
        />
    );
};

const PaginationLib = ({
    onPageChange,
    total,
    initialPage = 1,
    itemsCountPerPage = runtimeConfig.itemsCountPerPage,
    pagesToDisplay = 5,
    firstPageText = '«',
    prevPageText = '<i class="fa fa-chevron-left"></i>',
    nextPageText = '<i class="fa fa-chevron-right"></i>',
    lastPageText = '»',
    align = 'left',
    showFirstLast = true,
    showPrevNext = true,
    showInfo = true,
    loading = false,
    t
}) => {
    const [page, setPage] = useState(initialPage);
    const [loadingState, setLoadingState] = useState(loading);

    useEffect(() => {
        setPage(initialPage);
        setLoadingState(loading);
    }, [initialPage, loading]);

    const handlePageChange = useCallback((newPage) => {
        if (page === newPage) {
            return;
        }

        setPage(newPage);
        onPageChange(newPage);
    }, [page, onPageChange]);

    const getInfo = useCallback(() => {
        const staticNames = (t && t.currencyPage) || {};
        const to = page * itemsCountPerPage;
        const totalItems = itemsCountPerPage * total;

        return template(staticNames.paginationInfo || '', {
            from: (page - 1) * itemsCountPerPage + 1,
            to: to > totalItems ? totalItems : to,
            total: totalItems
        });
    }, [page, itemsCountPerPage, total, t]);

    const paginationItem = useCallback((className, pageNum, text) => {
        return (
            <PaginationItem
                className={className}
                onPageChange={handlePageChange}
                page={pageNum}
                display={text}
                key={text}
                loading={loadingState}
            />
        );
    }, [handlePageChange, loadingState]);

    const currentPage = page;
    const totalPages = Math.ceil(total);

    const pages = pageRange(currentPage, totalPages, pagesToDisplay)
        .map((p) => {
            const isCurrentPage = currentPage === p;
            return paginationItem(isCurrentPage ? 'active' : '', p, p);
        });

    const firstPage = showFirstLast && currentPage !== 1 ?
        paginationItem('first', 1, firstPageText) : null;

    const prevPage = showPrevNext && currentPage > 1 ?
        paginationItem('prev', currentPage - 1, prevPageText) : null;

    const nextPage = showPrevNext && currentPage !== totalPages ?
        paginationItem('next', currentPage + 1, nextPageText) : null;

    const lastPage = showFirstLast && currentPage !== totalPages ?
        paginationItem('last', totalPages, lastPageText) : null;

    return (
        <div className="pagination">
            {showInfo && align === 'right'
                ? <div className="pagination-info">{getInfo()}</div>
                : null
            }
            <div className="btn-group pagination-group">
                {firstPage}
                {prevPage}
                {pages}
                {nextPage}
                {lastPage}
            </div>
            {showInfo && align === 'left'
                ? <div className="pagination-info">{getInfo()}</div>
                : null
            }
        </div>
    );
};

PaginationLib.propTypes = {
    onPageChange: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    initialPage: PropTypes.number,
    itemsCountPerPage: PropTypes.number,
    pagesToDisplay: PropTypes.number,
    firstPageText: PropTypes.string,
    prevPageText: PropTypes.string,
    nextPageText: PropTypes.string,
    lastPageText: PropTypes.string,
    align: PropTypes.string,
    showFirstLast: PropTypes.bool,
    showPrevNext: PropTypes.bool,
    showInfo: PropTypes.bool,
    loading: PropTypes.bool,
    t: PropTypes.object
};

const Pagination = ({ totalPages, loading, ...props }) => {
    return (
        totalPages > 1 && !loading
            ? <PaginationLib
                {...props}
                total={totalPages}
              />
            : null
    );
};

export default withTranslation(Pagination);
